import queryString from 'query-string'
import { isEmpty } from 'lodash'

import { toast } from '@/components/toasts/toast.helpers'
import { getAgentOptions } from '@/utils/helpers'
import { fetchingAPI, apiService } from '../../api'
import { apiErrorMessage } from '../../api/handleResponse'
import {
  setTools,
  setToolsLoading,
  setAgentOptions,
  setPlaybookOptions,
  setEditingTool,
  setToolFormOrganization,
  setToolFieldTypes,
} from './tools.redux'
import { buildDuplicateToolPayload } from './tools.helpers'

// A config the API refused comes back as a 400 with the problems by path; the editor shows
// those on their rows, so only failures without them get the generic toast here.
const toastSaveFailure = (error, fallback) => {
  if (isEmpty(error?.errors)) {
    toast.error(apiErrorMessage(error, fallback), { cause: error })
  }
}

export const fetchTools = (orgId) => async (dispatch) => {
  dispatch(setToolsLoading({ fetchTools: true }))

  try {
    const tools = await fetchingAPI(
      `${apiService.summary}/tools?requested_organization_id=${orgId}`,
      'GET',
      dispatch
    )

    dispatch(setTools(tools))
  } catch (error) {
    toast.error('Failed to fetch tools', { cause: error })
  } finally {
    dispatch(setToolsLoading({ fetchTools: false }))
  }
}

export const fetchTool = (toolId) => async (dispatch) => {
  dispatch(setToolsLoading({ fetchTool: true }))

  try {
    const tool = await fetchingAPI(`${apiService.summary}/tools/${toolId}`, 'GET', dispatch)

    dispatch(setEditingTool(tool))
  } catch (error) {
    toast.error('Failed to fetch tool', { cause: error })
  } finally {
    dispatch(setToolsLoading({ fetchTool: false }))
  }
}

export const fetchToolFormOptions = (orgId) => async (dispatch, getState) => {
  // Switching the org picker twice in quick succession leaves two of these in
  // flight, and the slower one must not land on top of the newer selection.
  // The id is compared as a string because the filter starts out as ''.
  const stillSelected = () => String(getState().tools.filters.organization_id) === String(orgId)

  // Cleared up front so the previous organization's feature flags can't be read
  // as the newly selected one's while this request is in flight.
  dispatch(setToolFormOrganization(null))

  try {
    const organization = await fetchingAPI(
      `${apiService.core}/api/organizations/${orgId}`,
      'GET',
      dispatch
    )

    if (stillSelected()) {
      dispatch(setToolFormOrganization(organization))
    }
  } catch (error) {
    // Fail closed: only the SharePoint gate reads this, and the rest of the
    // form still loads without it. Surfaced rather than swallowed, or an author
    // whose organization happens to be AI Answers enabled would just find the
    // option quietly missing.
    toast.error('Failed to fetch organization settings', { cause: error })
  }

  try {
    const [usersResponse, playbooks] = await Promise.all([
      fetchingAPI(`${apiService.core}/api/organizations/${orgId}/users`, 'GET', dispatch),
      fetchingAPI(
        `${apiService.core}/api/${orgId}/configs?${queryString.stringify({ requested_properties: 'id,name,cid' })}&active_only=True`,
        'GET',
        dispatch
      ),
    ])

    if (!stillSelected()) {
      return
    }

    dispatch(setAgentOptions(getAgentOptions(usersResponse.users)))
    dispatch(
      setPlaybookOptions(
        playbooks
          .map((playbook) => ({
            value: playbook.cid,
            id: playbook.id,
            label: playbook.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      )
    )
  } catch (error) {
    toast.error('Failed to fetch form options', { cause: error })
  }
}

export const fetchToolFieldTypes = () => async (dispatch) => {
  dispatch(setToolsLoading({ fetchFieldTypes: true }))

  try {
    const registry = await fetchingAPI(`${apiService.summary}/tools/field_types`, 'GET', dispatch)

    dispatch(setToolFieldTypes(registry))
  } catch (error) {
    toast.error('Failed to fetch field types', { cause: error })
  } finally {
    dispatch(setToolsLoading({ fetchFieldTypes: false }))
  }
}

export const createTool = (payload, orgId) => async (dispatch) => {
  dispatch(setToolsLoading({ createTool: true }))

  try {
    const body = JSON.stringify({
      ...payload,
      requested_organization_id: orgId,
    })

    const tool = await fetchingAPI(`${apiService.summary}/tools`, 'POST', dispatch, body)

    toast.success('Tool created successfully')
    return { ok: true, tool }
  } catch (error) {
    toastSaveFailure(error, 'Failed to create tool')
    return { ok: false, error }
  } finally {
    dispatch(setToolsLoading({ createTool: false }))
  }
}

export const updateTool = (toolId, payload) => async (dispatch) => {
  dispatch(setToolsLoading({ updateTool: true }))

  try {
    const body = JSON.stringify(payload)

    const tool = await fetchingAPI(`${apiService.summary}/tools/${toolId}`, 'PUT', dispatch, body)

    toast.success('Tool updated successfully')
    return { ok: true, tool }
  } catch (error) {
    toastSaveFailure(error, 'Failed to update tool')
    return { ok: false, error }
  } finally {
    dispatch(setToolsLoading({ updateTool: false }))
  }
}

export const duplicateTool = (tool, existingNames) => async (dispatch) => {
  dispatch(setToolsLoading({ duplicateTool: tool.id }))

  try {
    const body = JSON.stringify({
      ...buildDuplicateToolPayload(tool, existingNames),
      requested_organization_id: tool.organization_id,
    })

    const copy = await fetchingAPI(`${apiService.summary}/tools`, 'POST', dispatch, body)

    toast.success('Tool duplicated')
    return { ok: true, tool: copy }
  } catch (error) {
    toast.error(apiErrorMessage(error, 'Failed to duplicate tool'))
    return { ok: false, error }
  } finally {
    dispatch(setToolsLoading({ duplicateTool: null }))
  }
}

export const deleteTool = (toolId, orgId) => async (dispatch) => {
  dispatch(setToolsLoading({ deleteTool: true }))

  try {
    await fetchingAPI(`${apiService.summary}/tools/${toolId}`, 'DELETE', dispatch)

    toast.success('Tool deleted successfully')
    await dispatch(fetchTools(orgId))
  } catch (error) {
    toast.error('Failed to delete tool', { cause: error })
  } finally {
    dispatch(setToolsLoading({ deleteTool: false }))
  }
}
